/* eslint-disable max-lines */
/* eslint-disable no-use-before-define */
import { takeEvery, put, call, all, select } from "redux-saga/effects";

import {
  fetchCitySearchSuggestions,
  setOrderItemAttribute,
  getSenderDetails,
  getOccasionsAndTemplate,
  getCardMessageDetails,
  getCartAmountSummary,
  setCardMessageDetails,
  applyCoupon,
  removeAppliedCoupon,
  getCouponList,
  fetchSpecialCountryApi,
  setAddressFormForItem,
  deleteAddressFormForItem,
  getDonationStatus,
  getDonationAmount,
  addDonationAmount,
  removeDonationAmount,
  getCarryBagInfo,
  getCarryBagAmount,
  addCarryBagAmount,
  removeCarryBagAmount,
  getCryDonationInfo,
  setUpdateCartFromDaPage,
  resetDelivery,
  getProductItemQuantity,
  sendMobileOtp,
  verifyMobileOtp,
} from "../../../api/deliveryAddressApi";
import {
  requestToDeleteCartItem,
  requestToFetchCitySuggestionDetails,
  setCitySuggestion,
  requestToSetCartItemDetailsOnDA,
  requestToApplyCheckoutDeliveryDateUpdates,
  setCompleteCartDetails,
  setSenderDetails,
  setItemAttributeForOrderItem,
  requestToFetchOccasionAndTemplates,
  setListOfOccasionAndMsgTemplate,
  requestToFetchMessageDetails,
  setMessageCardDetails,
  requestToFetchCartAmountSummary,
  requestToSyncAutoCombinedCartSupplier,
  requestToSetCardDetails,
  requestToInitiateCheckoutPageDA,
  setCartItemExpiryDetails,
  setCartItemExpiryIndex,
  setCartItemExpiredDetails,
  requestToSetCoupon,
  responseFromSetCoupon,
  requestToRemoveCoupon,
  responseToRemoveCoupon,
  setSpecialCountry,
  requestToPostAddressForItem,
  requestToDeleteAddressForItem,
  setCartAmountSummary,
  requestToSetDonationStatus,
  setDonationStatus,
  setDonationAmount,
  responseToAddDonationAmount,
  responseToRemoveDonationAmount,
  setCarryBagInfo,
  requestToFetchCarryBagInfo,
  setCarryBagAmount,
  requestToFetchCarryBagAmount,
  responseToRemoveCarryBag,
  responseToAddCarryBag,
  setDonationInfo,
  requestToFetchDonationInfo,
  requestToFetchCouponList,
  setCouponList,
  setErrorMsg,
  requestToUpdateCartDataFromDA,
  setIsCouponApplied,
  setIsProceedToPayError,
  setIsSetCartItemValuesError,
  setAddressLoading,
  initiateCompleteCartLoader,
  setCartSummaryLoader,
  requestToFetchSpecialCountries,
  requestToResetDelivery,
  setProductItemQuantityLimit,
  setCheckoutLandingServiceability,
  setShowCombinedDeliveryAnimation,
  requestToSendSenderMobileOtp,
  setSenderMobileOtpSent,
  requestToVerifySenderMobileOtp,
  setSenderMobileOtpVerified,
  setSenderMobileOtpError,
} from "../../slices/deliveryAddress";
import { deleteCartItemService, getCartService, setCartItemValuesService } from "../../../api/cartApi";
import updateCartDetails from "../../../utils/updateCartDetailsJSON";
import { fetchFnpCashBalance, fetchFnpCashLabelInfo } from "../fnpCashSaga";
import { setCurrencyListToStoreSaga, initiateFusCall, fetchCurrencyFlagIcon } from "../commonSaga";
import { ROUTE_CONSTANTS } from "../../../constants/route";
import { fetchWebFeatureControlsSaga } from "../webFeatureControls";
import { getCustomerLoginDetails } from "../loginSaga";
import {
  DELIVERY_HAS_COMBINED,
  IS_NOT_VALID_FOR_THIS_ORDER,
  RESET_DELIVERY_COOKIE,
  SENDER_MOBILE_OTP,
  YOUR_COUPON_CODE,
} from "../../../constants/pageConstants/deliveryAddressPageConstant";
import CookieUtil from "../../../utils/storage/cookieUtil";
import {
  evaluateCheckoutLandingServiceability,
  buildUnserviceableCartItemsPayload,
  getUnserviceableCartItemIndexesToSync,
  getServiceableCartItemIndexesToSync,
  getMissingAddressCartItemIndexesToSync,
  getCartItemIndexesToSyncPincodeFromFirstItem,
  getCartItemIndexesToSyncPincodeFromSavedAddress,
  buildSetAddressForMissingItemsPayload,
  buildPincodeSyncForCartItemsPayload,
  buildBatchedCheckoutDeliveryDateUpdatePayloads,
} from "../../../utils/checkout/deliveryAddress";
import { buildAutoCombinedCartSupplierPayload } from "../../../utils/checkout/deliveryDateAndTimeSlot";
import { resolveLatLongForServiceability } from "../helpers/savedAddressLatLong";

/**
 * Aligns cart items to the first item (latest added): pincode-only when index 0 has no saved
 * address, or saved address via setAddressForItem-rj when index 0 has a saved address.
 *
 * @generator
 * @param {Array} orderList
 * @returns {Object|null} Updated cart data when sync succeeds
 */
function* syncMissingAddressFromExistingItemIfNeeded(orderList = []) {
  const pincodeSyncInfo =
    getCartItemIndexesToSyncPincodeFromFirstItem(orderList) ||
    getCartItemIndexesToSyncPincodeFromSavedAddress(orderList);

  if (pincodeSyncInfo) {
    const pincodePayload = buildPincodeSyncForCartItemsPayload(pincodeSyncInfo);
    const pincodeResponse = yield call(setCartItemValuesService, pincodePayload);

    if (!pincodeResponse?.isSuccess) {
      return null;
    }

    yield call(resetDeliverySaga, false);
    const latLong = yield call(resolveLatLongForServiceability);
    const cartResponse = yield call(getCartService, { payload: "N", isDomestic: true, ...latLong });
    return cartResponse?.data?.isSuccess ? cartResponse.data : null;
  }

  const syncInfo = getMissingAddressCartItemIndexesToSync(orderList);

  if (!syncInfo) {
    return null;
  }

  const payload = buildSetAddressForMissingItemsPayload(syncInfo);
  const response = yield call(setAddressFormForItem, payload);

  if (!response?.isSuccess) {
    return null;
  }

  yield call(resetDeliverySaga, false);
  const latLong = yield call(resolveLatLongForServiceability);
  const cartResponse = yield call(getCartService, { payload: "N", isDomestic: true, ...latLong });
  return cartResponse?.data?.isSuccess ? cartResponse.data : null;
}

/**
 * Syncs cart item unserviceable flags via setCartItemValues API after serviceability evaluation.
 * Marks newly OOS items as Y and previously OOS items that are serviceable again as N.
 *
 * @generator
 * @param {{ outOfStockByCartItemIndex: Object, orderList: Array }} params
 */
function* syncUnserviceableCartItemsIfNeeded({ outOfStockByCartItemIndex = {}, orderList = [] }) {
  const indexesToMarkUnserviceable = getUnserviceableCartItemIndexesToSync(outOfStockByCartItemIndex, orderList);
  const indexesToMarkServiceable = getServiceableCartItemIndexesToSync(outOfStockByCartItemIndex, orderList);

  if (!indexesToMarkUnserviceable.length && !indexesToMarkServiceable.length) {
    return;
  }

  let didSync = false;

  if (indexesToMarkUnserviceable.length) {
    const unserviceablePayload = buildUnserviceableCartItemsPayload(indexesToMarkUnserviceable, "Y");
    const unserviceableResponse = yield call(setCartItemValuesService, unserviceablePayload);

    if (unserviceableResponse?.isSuccess) {
      didSync = true;
    }
  }

  if (indexesToMarkServiceable.length) {
    const serviceablePayload = buildUnserviceableCartItemsPayload(indexesToMarkServiceable, "N");
    const serviceableResponse = yield call(setCartItemValuesService, serviceablePayload);

    if (serviceableResponse?.isSuccess) {
      didSync = true;
    }
  }

  if (didSync) {
    yield put(setCartItemExpiryIndex(null));
    const latLong = yield call(resolveLatLongForServiceability);
    yield call(fetchCompleteCartDetailsOnDeliveryPage, {
      skipUnserviceableSync: true,
      skipAddressSync: true,
      silentRefresh: true,
      skipServiceabilityEvaluation: true,
      ...latLong,
    });
    yield call(fetchCartAmountSummarySaga);
  }
}

/**
 * Saga to fetch complete cart details on delivery address.
 *
 * @generator
 * @function fetchCompleteCartDetailsOnDeliveryPage
 * @param {{ skipUnserviceableSync?: boolean, skipAddressSync?: boolean, silentRefresh?: boolean, skipServiceabilityEvaluation?: boolean }} [options]
 */
export function* fetchCompleteCartDetailsOnDeliveryPage(options = {}) {
  const {
    skipUnserviceableSync = false,
    skipAddressSync = false,
    silentRefresh = false,
    skipServiceabilityEvaluation = false,
  } = options;
  try {
    if (!silentRefresh) {
      yield put(initiateCompleteCartLoader());
    }
    const latLong = yield call(resolveLatLongForServiceability);
    const response = yield call(getCartService, { payload: "N", isDomestic: true, ...latLong });
    if (response?.data?.isSuccess) {
      let cartData = response?.data;

      yield call(setCurrencyListToStoreSaga, {});

      if (!skipAddressSync && cartData?.orderList?.length > 1) {
        const syncedCartData = yield call(syncMissingAddressFromExistingItemIfNeeded, cartData.orderList);
        if (syncedCartData) {
          cartData = syncedCartData;
        }
      }

      if (!skipServiceabilityEvaluation) {
        const serviceabilityEvaluation = yield call(evaluateCheckoutLandingServiceability, cartData?.orderList);

        yield put(
          setCheckoutLandingServiceability({
            outOfStockByCartItemIndex: serviceabilityEvaluation.outOfStockByCartItemIndex,
            serviceItemsByPinCode: serviceabilityEvaluation.serviceItemsByPinCode,
          }),
        );
        yield put(setCompleteCartDetails(cartData));

        if (!skipUnserviceableSync) {
          yield call(syncUnserviceableCartItemsIfNeeded, {
            outOfStockByCartItemIndex: serviceabilityEvaluation.outOfStockByCartItemIndex,
            orderList: cartData?.orderList,
          });
        }
      } else {
        yield put(setCompleteCartDetails(cartData));
      }

      if (cartData?.getCartExpiry?.isSuccess) {
        yield put(setCartItemExpiryDetails(cartData?.getCartExpiry?.details));
      } else if (cartData?.getCartExpiry === null) {
        yield put(setCartItemExpiryDetails(null));
      }
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * Saga to fetch current sender details.
 *
 * @generator
 * @function fetchSenderDetailsDeliveryPage
 *
 */
function* fetchSenderDetailsDeliveryPage() {
  try {
    const response = yield call(getSenderDetails);

    if (response?.isSuccess) {
      yield put(setSenderDetails(response));
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * Saga to fetch search city suggestion list.
 *
 * @generator
 * @function fetchSenderCitySuggestion
 *
 */
function* fetchSenderCitySuggestion(value) {
  try {
    const response = yield call(fetchCitySearchSuggestions, value.payload);
    if (response?.isSuccess) {
      yield put(setCitySuggestion(response?.data));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to fetch special country list for.
 *
 * @generator
 * @function fetchSpecialCountries
 *
 */
function* fetchSpecialCountries() {
  try {
    const response = yield call(fetchSpecialCountryApi);
    if (response && response.value) {
      yield put(setSpecialCountry(response.value));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * Saga to fetch complete cart amount summary.
 * @generator
 * @function fetchCartAmountSummarySaga
 */
export function* fetchCartAmountSummarySaga() {
  try {
    yield put(setCartSummaryLoader(true));
    const response = yield call(getCartAmountSummary);
    const donationStatus = yield select((state) => state.deliveryAddress.donationStatus);
    const carryBagAmount = yield select((state) => state.deliveryAddress.carryBagAmount);
    if (response?.isSuccess) {
      yield put(setCartSummaryLoader(false));
      yield put(
        setCartAmountSummary(
          updateCartDetails({
            ...response.priceDetails,
            donationStatus,
            carryBagAmount,
          }),
        ),
      );
    } else {
      yield put(setCartSummaryLoader(false));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Syncs resolved supplier party id for auto-combined cart items, then refreshes cart summary only.
 *
 * @generator
 * @param {{ payload: { orderList: Array, resolvedSupplierPartyId: string|number } }} action
 */
function* syncAutoCombinedCartSupplierSaga({ payload }) {
  try {
    const formPayload = buildAutoCombinedCartSupplierPayload(payload);

    if (!formPayload) {
      return;
    }

    const response = yield call(setCartItemValuesService, formPayload);

    if (response?.isSuccess) {
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to save address.
 *
 * @generator
 * @function postAddressForItem
 *
 */
function* postAddressForItem({ payload }) {
  try {
    const { isInternationalProduct, isDifferentPincode, payload: payloadData } = payload;
    yield put(setAddressLoading(true));
    const response = yield call(setAddressFormForItem, payloadData);
    if (response?.isSuccess) {
      if (!isInternationalProduct || (isInternationalProduct && isDifferentPincode)) {
        yield call(resetDeliverySaga);
      } else {
        yield call(fetchCompleteCartDetailsOnDeliveryPage);
        yield call(fetchCartAmountSummarySaga);
      }
    }
  } catch (error) {
    console.log(error);
  } finally {
    yield put(setAddressLoading(false));
  }
}
/**
 * Saga to update cart details of click of proceed to pay.
 *
 * @generator
 * @function postUpdateCartItem
 *
 */
function* postUpdateCartItem({ payload }) {
  try {
    const response = yield call(setUpdateCartFromDaPage, payload);
    if (response?.isSuccess) {
      window.location.href = `/${ROUTE_CONSTANTS.CHECKOUT}/${ROUTE_CONSTANTS.PAYMENT_OPTIONS}`;
      return;
    }

    yield put(setIsProceedToPayError(true));
  } catch (error) {
    console.log(error);
    yield put(setIsProceedToPayError(true));
  }
}

/**
 *
 * @param {object} action action param
 */
function* deleteGiftItemSaga(action) {
  try {
    const encoded = new URLSearchParams(action.payload).toString();
    const response = yield deleteCartItemService(encoded);
    if (response?.isSuccess) {
      if (action.payload?.isAddon) {
        yield call(fetchCompleteCartDetailsOnDeliveryPage);
        yield call(fetchCartAmountSummarySaga);
      } else {
        yield call(fetchCompleteCartDetailsOnDeliveryPage);
        yield call(fetchCartAmountSummarySaga);
      }
      yield call(initiateFusCall);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 */
function* setItemAttributeToOrderItem({ payload }) {
  try {
    yield call(setOrderItemAttribute, payload);
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to fetch complete message card details.
 *
 * @generator
 * @function fetchMessageCardDetails
 *
 */
function* fetchMessageCardDetails({ payload }) {
  try {
    const response = yield call(getCardMessageDetails, payload);

    if (response?.isSuccess) {
      yield put(setMessageCardDetails(response));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to set complete message card details.
 *
 * @generator
 * @function setFreeMessageCardDetails
 *
 */
function* setFreeMessageCardDetails({ payload }) {
  try {
    const response = yield setCardMessageDetails(payload);
    if (response?.success) {
      yield call(fetchCompleteCartDetailsOnDeliveryPage);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to fetch complete occasion and message template.
 *
 * @generator
 * @function fetchOccasionAndMessageTemplates
 *
 */
function* fetchOccasionAndMessageTemplates() {
  try {
    const response = yield call(getOccasionsAndTemplate);

    if (response?.isSuccess) {
      yield put(setListOfOccasionAndMsgTemplate(response.data));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to set complete message card details.
 *
 * @generator
 * @function setApplyCoupon
 *
 */
function* setApplyCoupon({ payload }) {
  try {
    const response = yield applyCoupon(payload);
    if (response?.isSuccess) {
      if (response?.totalDiscountAmount !== 0) {
        yield put(responseFromSetCoupon(response));
        yield put(setErrorMsg(""));
        yield put(setIsCouponApplied(true));
        yield call(fetchCartAmountSummarySaga);
      } else {
        yield put(setErrorMsg(`${YOUR_COUPON_CODE} ${response?.couponCode} ${IS_NOT_VALID_FOR_THIS_ORDER}`));
      }
    } else {
      const errorMsg = response?.errorMsg;
      if (response?.totalDiscountAmount === 0 && response?.couponCode) {
        yield put(setErrorMsg(`${YOUR_COUPON_CODE} ${response?.couponCode} ${IS_NOT_VALID_FOR_THIS_ORDER}`));
      } else {
        yield put(setErrorMsg(errorMsg));
      }
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * Saga to set complete message card details.
 *
 * @generator
 * @function setApplyCoupon
 *
 */
function* setRemoveAppliedCoupon({ payload }) {
  try {
    const response = yield removeAppliedCoupon(payload);
    if (response?.isSuccess) {
      yield put(setIsCouponApplied(false));
      yield put(responseToRemoveCoupon(response));
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * Saga to fetch donation status.
 * @generator
 * @function fetchDonationStatus
 */
function* fetchDonationStatus() {
  try {
    const response = yield call(getDonationStatus);

    if (response) {
      yield put(setDonationStatus(response.value));
      if (response.value === "Y") {
        const resp = yield call(getDonationAmount);
        if (resp) {
          yield put(setDonationAmount(resp.value));
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 *
 * Saga to add donation amount.
 * @generator
 * @function addDonationAmountSaga
 */
function* addDonationAmountSaga() {
  try {
    yield put(setCartSummaryLoader(true));
    const donationAmount = yield select((state) => state.deliveryAddress.donationAmount);
    const customerID = yield select((state) => state.loginSignUp.userDetails.customerID);
    const isAgentLogin = yield select((state) => state.loginSignUp.userDetails.isAgentLogin);
    const response = yield call(addDonationAmount, donationAmount);

    if (response?.isSuccess) {
      const orderGrandTotal = response?.total;
      const priceDetails = yield select((state) => state.deliveryAddress.cartAmountSummary);
      yield call(
        fetchFnpCashBalance,
        isAgentLogin === "true" ? { payload: { isAgent: true, partyId: customerID } } : {},
      );
      yield put(setCartAmountSummary(updateCartDetails({ ...priceDetails, donationAmount, orderGrandTotal })));
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 *
 * Saga to add remove amount.
 * @generator
 * @function removeDonationAmountSaga
 */
function* removeDonationAmountSaga() {
  try {
    yield put(setCartSummaryLoader(true));
    const donationAmount = yield select((state) => state.deliveryAddress.donationAmount);
    const customerID = yield select((state) => state.loginSignUp.userDetails.customerID);
    const isAgentLogin = yield select((state) => state.loginSignUp.userDetails.isAgentLogin);
    const response = yield call(removeDonationAmount, donationAmount);

    if (response?.isSuccess) {
      const donationAmount = 0;
      const orderGrandTotal = response?.total;
      const priceDetails = yield select((state) => state.deliveryAddress.cartAmountSummary);
      yield call(
        fetchFnpCashBalance,
        isAgentLogin === "true" ? { payload: { isAgent: true, partyId: customerID } } : {},
      );
      yield put(setCartAmountSummary(updateCartDetails({ ...priceDetails, donationAmount, orderGrandTotal })));
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * This function gets the CRY donation info if it is required to render or not.
 *
 * @returns {string}
 */
function* fetchDonationInfo() {
  try {
    const response = yield call(getCryDonationInfo);
    if (response) {
      yield put(setDonationInfo(response));
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * This function gets the carry bag info if it is required to render or not.
 *
 * @returns {string}
 */
function* fetchCarryBagInfo() {
  try {
    const response = yield call(getCarryBagInfo);
    if (response) {
      yield put(setCarryBagInfo(response));
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * This function gets the carry bag info if it is required to render or not.
 *
 * @returns {string}
 */
function* fetchCarryBagAmount() {
  try {
    const response = yield call(getCarryBagAmount);

    if (response) {
      yield put(setCarryBagAmount(response?.value));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * Saga to add carry bag amount.
 * @generator
 * @function addCarryBagAmountSaga
 */
function* addCarryBagAmountSaga() {
  try {
    yield put(setCartSummaryLoader(true));
    const carryBagAmount = yield select((state) => state.deliveryAddress.carryBagAmount);
    const customerID = yield select((state) => state.loginSignUp.userDetails.customerID);
    const isAgentLogin = yield select((state) => state.loginSignUp.userDetails.isAgentLogin);
    const response = yield call(addCarryBagAmount, carryBagAmount);

    if (response?.isSuccess) {
      const orderGrandTotal = response?.total;
      const priceDetails = yield select((state) => state.deliveryAddress.cartAmountSummary);
      yield call(
        fetchFnpCashBalance,
        isAgentLogin === "true" ? { payload: { isAgent: true, partyId: customerID } } : {},
      );
      yield put(setCartAmountSummary(updateCartDetails({ ...priceDetails, orderGrandTotal })));
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 *
 * Saga to remove carry bag amount.
 * @generator
 * @function addCarryBagAmountSaga
 */
function* removeCarryBagAmountSaga() {
  try {
    yield put(setCartSummaryLoader(true));
    const carryBagAmount = yield select((state) => state.deliveryAddress.carryBagAmount);
    const customerID = yield select((state) => state.loginSignUp.userDetails.customerID);
    const isAgentLogin = yield select((state) => state.loginSignUp.userDetails.isAgentLogin);
    const response = yield call(removeCarryBagAmount, carryBagAmount);

    if (response?.isSuccess) {
      const orderGrandTotal = response?.total;
      const priceDetails = yield select((state) => state.deliveryAddress.cartAmountSummary);
      yield call(
        fetchFnpCashBalance,
        isAgentLogin === "true" ? { payload: { isAgent: true, partyId: customerID } } : {},
      );
      yield put(setCartAmountSummary(updateCartDetails({ ...priceDetails, orderGrandTotal })));
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * Saga to fetch existing coupons.
 * @generator
 * @function fetchCouponListSaga
 */
function* fetchCouponListSaga() {
  try {
    const response = yield call(getCouponList);

    if (response) {
      yield put(setCouponList(response));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 *
 * Saga to fetch product item quantity.
 * @generator
 * @function fetchProductItemQuantitySaga
 */
function* fetchProductItemQuantitySaga() {
  try {
    const response = yield call(getProductItemQuantity);

    if (response) {
      yield put(setProductItemQuantityLimit(response?.value));
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * this function get the delivery address common API call called on load
 *
 * @returns {object} checkout page data cartItems details,sender details,t&c etc.,
 */
function* fetchAllDAPageCallOnLoad() {
  yield all([
    call(initiateFusCall),
    call(fetchCompleteCartDetailsOnDeliveryPage),
    call(fetchSenderDetailsDeliveryPage),
    call(getCustomerLoginDetails),
    call(fetchSpecialCountries),
    call(fetchCartAmountSummarySaga),
    call(fetchFnpCashLabelInfo),
    call(fetchDonationStatus),
    call(fetchDonationInfo),
    call(fetchCarryBagInfo),
    call(fetchCarryBagAmount),
    call(fetchWebFeatureControlsSaga),
    call(fetchCurrencyFlagIcon),
    call(fetchProductItemQuantitySaga),
  ]);
}

/**
 *
 */
function* setCartItemDetailsOnDA(action) {
  try {
    const response = yield call(setCartItemValuesService, action.payload);
    const cartItemParams = new URLSearchParams(action.payload);
    const hasNewTimeSlot = Boolean(cartItemParams.get("timeSlotId"));
    // Pincode-only cart updates need a fresh serviceability pass; date/slot updates do not.
    const isPincodeOnlyUpdate =
      Boolean(cartItemParams.get("pincode")) &&
      !cartItemParams.get("timeSlotId") &&
      !cartItemParams.get("shippingMethodId") &&
      !cartItemParams.get("deliveryDate");
    const skipServiceabilityEvaluation = !isPincodeOnlyUpdate && !action.meta?.forceServiceabilityEvaluation;

    if (hasNewTimeSlot) {
      yield put(setCartItemExpiredDetails(null));
      yield put(setCartItemExpiryIndex(null));
    }

    if (response?.isSuccess) {
      const isCombineAnimationFlow = Boolean(action.meta?.showCombinedDeliveryAnimation);

      if (isCombineAnimationFlow) {
        yield put(setShowCombinedDeliveryAnimation(true));
      }

      yield call(fetchCompleteCartDetailsOnDeliveryPage, {
        silentRefresh: isCombineAnimationFlow || skipServiceabilityEvaluation,
        skipServiceabilityEvaluation,
        skipUnserviceableSync: skipServiceabilityEvaluation,
      });
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
    yield put(setIsSetCartItemValuesError(true));
  }
}

/**
 * Batches delivery-date nudge setCartItemValues calls, then refreshes cart once without
 * re-hitting products/serviceable (landing/address evaluation already produced these dates).
 *
 * @generator
 * @param {{ payload: { rescheduledItems: Array, pinCode: string } }} action
 */
function* applyCheckoutDeliveryDateUpdatesSaga(action) {
  try {
    const { rescheduledItems = [], pinCode = "" } = action.payload || {};
    const payloads = buildBatchedCheckoutDeliveryDateUpdatePayloads(rescheduledItems, pinCode);

    if (!payloads.length) {
      return;
    }

    for (const payload of payloads) {
      yield call(setCartItemValuesService, payload);
    }

    yield call(fetchCompleteCartDetailsOnDeliveryPage, {
      silentRefresh: true,
      skipServiceabilityEvaluation: true,
      skipUnserviceableSync: true,
      skipAddressSync: true,
    });
    yield call(fetchCartAmountSummarySaga);
  } catch (error) {
    console.log(error);
  }
}
/**
 * Saga to delete address Form.
 *
 * @generator
 * @function toDeleteAddressForm
 *
 */
function* toDeleteAddressForm({ payload }) {
  try {
    const response = yield call(deleteAddressFormForItem, payload);
    if (response?.isSuccess) {
      yield call(fetchCompleteCartDetailsOnDeliveryPage);
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}
/**
 * Saga to reset deliveries.
 *
 * @generator
 * @function resetDeliverySaga
 */
function* resetDeliverySaga(isAllowCompleteCartFetch = true) {
  try {
    const response = yield call(resetDelivery);
    if (response?.isSuccess) {
      CookieUtil.deleteCookie(RESET_DELIVERY_COOKIE);
      CookieUtil.deleteCookie(DELIVERY_HAS_COMBINED);
      yield put(setShowCombinedDeliveryAnimation(false));
      if (isAllowCompleteCartFetch) {
        yield call(fetchCompleteCartDetailsOnDeliveryPage);
      }
      yield call(fetchCartAmountSummarySaga);
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Saga to send the OTP on the sender's mobile number.
 *
 * @generator
 * @function sendSenderMobileOtpSaga
 * @param {Object} action - Action holding mobileNumber, countryCode and deviceType.
 */
function* sendSenderMobileOtpSaga({ payload }) {
  try {
    const formData = new URLSearchParams();
    formData.append("MOBILENUMBER", payload?.mobileNumber);
    formData.append("countryCode", payload?.countryCode);
    formData.append("deviceType", payload?.deviceType);

    const response = yield call(sendMobileOtp, formData);

    if (response?.responseStatus === "success" || response?.response === "success") {
      yield put(setSenderMobileOtpSent());
    } else {
      yield put(setSenderMobileOtpError(response?.errorMessage || response?.msg || SENDER_MOBILE_OTP.sendOtpError));
    }
  } catch (error) {
    yield put(setSenderMobileOtpError(SENDER_MOBILE_OTP.sendOtpError));
  }
}

/**
 * Saga to verify the OTP sent on the sender's mobile number.
 *
 * @generator
 * @function verifySenderMobileOtpSaga
 * @param {Object} action - Action holding mobileNumber, countryCode, otp and username.
 */
function* verifySenderMobileOtpSaga({ payload }) {
  try {
    const formData = new URLSearchParams();
    formData.append("MOBILENUMBER", payload?.mobileNumber);
    formData.append("COUNTRYCODE", `${payload?.countryCode}`);
    formData.append("OTP", payload?.otp);

    if (payload?.username) {
      formData.append("USERNAME", payload.username);
    }

    const response = yield call(verifyMobileOtp, formData);

    if (response?.response === "success") {
      yield put(setSenderMobileOtpVerified({ mobileNumber: payload?.mobileNumber, countryCode: payload?.countryCode }));
    } else {
      yield put(setSenderMobileOtpError(response?.msg || SENDER_MOBILE_OTP.verifyOtpError));
    }
  } catch (error) {
    yield put(setSenderMobileOtpError(SENDER_MOBILE_OTP.verifyOtpError));
  }
}

/**
 * Root saga that watches for delivery-address related actions and triggers corresponding sagas.
 *
 * @generator
 * @function DeliveryAddressSaga
 */
function* watchDeliveryAddressSaga() {
  yield takeEvery(requestToDeleteCartItem.type, deleteGiftItemSaga);
  yield takeEvery(requestToFetchCitySuggestionDetails.type, fetchSenderCitySuggestion);
  yield takeEvery(requestToPostAddressForItem.type, postAddressForItem);
  yield takeEvery(requestToDeleteAddressForItem.type, toDeleteAddressForm);
  yield takeEvery(setItemAttributeForOrderItem.type, setItemAttributeToOrderItem);
  yield takeEvery(requestToSetCartItemDetailsOnDA.type, setCartItemDetailsOnDA);
  yield takeEvery(requestToApplyCheckoutDeliveryDateUpdates.type, applyCheckoutDeliveryDateUpdatesSaga);
  yield takeEvery(requestToFetchOccasionAndTemplates.type, fetchOccasionAndMessageTemplates);
  yield takeEvery(requestToFetchMessageDetails.type, fetchMessageCardDetails);
  yield takeEvery(requestToSetCardDetails.type, setFreeMessageCardDetails);
  yield takeEvery(requestToInitiateCheckoutPageDA.type, fetchAllDAPageCallOnLoad);
  yield takeEvery(requestToSetCoupon.type, setApplyCoupon);
  yield takeEvery(requestToRemoveCoupon.type, setRemoveAppliedCoupon);
  yield takeEvery(requestToFetchCartAmountSummary.type, fetchCartAmountSummarySaga);
  yield takeEvery(requestToSyncAutoCombinedCartSupplier.type, syncAutoCombinedCartSupplierSaga);
  yield takeEvery(requestToSetDonationStatus.type, fetchDonationStatus);
  yield takeEvery(responseToAddDonationAmount.type, addDonationAmountSaga);
  yield takeEvery(responseToRemoveDonationAmount.type, removeDonationAmountSaga);
  yield takeEvery(requestToFetchDonationInfo.type, fetchDonationInfo);
  yield takeEvery(requestToFetchCarryBagInfo.type, fetchCarryBagInfo);
  yield takeEvery(requestToFetchCarryBagAmount.type, fetchCarryBagAmount);
  yield takeEvery(responseToAddCarryBag.type, addCarryBagAmountSaga);
  yield takeEvery(responseToRemoveCarryBag.type, removeCarryBagAmountSaga);
  yield takeEvery(requestToFetchCouponList.type, fetchCouponListSaga);
  yield takeEvery(requestToUpdateCartDataFromDA.type, postUpdateCartItem);
  yield takeEvery(requestToFetchSpecialCountries.type, fetchSpecialCountries);
  yield takeEvery(requestToResetDelivery.type, resetDeliverySaga);
  yield takeEvery(requestToSendSenderMobileOtp.type, sendSenderMobileOtpSaga);
  yield takeEvery(requestToVerifySenderMobileOtp.type, verifySenderMobileOtpSaga);
}

export default watchDeliveryAddressSaga;
